import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import AlertBox from '../../Components/AlertBox';

const LeaveRequest = () => {
  // State for form data
  const [formData, setFormData] = useState({
    company: '',
    employee: '',
    reason: '',
    fromDate: '',
    toDate: '',
    startDate: '',
    endDate: '',
    contact: '',
    employeeName: '',
    leaveType: ''
  });

  // State for tracking if medical report should be shown
  const [showMedicalReport, setShowMedicalReport] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [employeeDetails, setEmployeeDetails] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const employeeId = localStorage.getItem("employeeId");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [showAlert, setShowAlert] = useState(null);
  // State for tracking which request is being edited
  const [editingId, setEditingId] = useState(null);

  // State for leave requests
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);

  // State for leave balance
  const [leaveBalance, setLeaveBalance] = useState({});

  // State for filters
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [filteredLeaveRequests, setFilteredLeaveRequests] = useState([]);

  // State for leave types
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loadingLeaveTypes, setLoadingLeaveTypes] = useState(false);
  const [leaveTypesError, setLeaveTypesError] = useState(null);

  const [selectedLeaveBalance, setSelectedLeaveBalance] = useState({
    total: 0,
    used: 0,
    remaining: 0
  });

  // NEW: Confirmation state for delete actions
  const [confirmAction, setConfirmAction] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Initialize selected leave balance when leave types or form data changes
  useEffect(() => {
    if (formData.leaveType && formData.leaveType.toLowerCase() === 'unpaid leave') {
      setSelectedLeaveBalance({ total: Infinity, used: 0, remaining: Infinity });
    } else if (formData.leaveType && leaveBalance[formData.leaveType]) {
      setSelectedLeaveBalance(leaveBalance[formData.leaveType]);
    } else if (leaveTypes.length > 0 && !formData.leaveType) {
      // Set default selected balance to first leave type
      const firstLeaveType = leaveTypes[0].leaveType || leaveTypes[0].name;
      if (leaveBalance[firstLeaveType]) {
        setSelectedLeaveBalance(leaveBalance[firstLeaveType]);
        // Also set the form data to first leave type
        setFormData(prev => ({
          ...prev,
          leaveType: firstLeaveType
        }));
      }
    }
  }, [leaveBalance, leaveTypes, formData.leaveType]);

  // Fetch leave types from API
  const fetchLeaveTypes = async (companyIdParam = null) => {
    console.log('🚀 FETCHING LEAVE TYPES FOR COMPANY...');
    setLoadingLeaveTypes(true);
    setLeaveTypesError(null);

    const token = localStorage.getItem("authToken") || localStorage.getItem("token");

    if (!token) {
      console.error('❌ No authentication token found');
      setLeaveTypesError('Authentication required. Please log in again.');
      setLoadingLeaveTypes(false);
      return;
    }

    let companyId = companyIdParam;

    // If no company ID passed as parameter, try to get from token
    if (!companyId) {
      try {
        const decoded = jwtDecode(token);
        console.log('LEAVE TYPES TOKEN DECODING:', {
          fullToken: decoded,
          companyId: decoded.companyId,
          companyObject: decoded.company,
          orgId: decoded.orgId
        });

        companyId = decoded.companyId || decoded.company?._id || decoded.company || decoded.orgId;
        console.log('EXTRACTED COMPANY ID:', companyId);
      } catch (err) {
        console.error("❌ Error decoding token for leave types:", err);
        setLeaveTypesError('Invalid authentication token. Please log in again.');
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
        setLoadingLeaveTypes(false);
        return;
      }
    }

    // If still no company ID, try to get from employee details
    if (!companyId && employeeDetails?.company) {
      companyId = employeeDetails.company._id || employeeDetails.company;
      console.log('COMPANY ID FROM EMPLOYEE DETAILS:', companyId);
    }

    if (!companyId) {
      console.error('❌ No company ID available');
      setLeaveTypesError('Company information not found. Please contact support.');
      setLoadingLeaveTypes(false);
      return;
    }

    try {
      console.log('🚀 FETCHING LEAVE TYPES FROM API:', `https://slateblue-mallard-430836.hostingersite.com/api/leave-types/company/${companyId}`);

      const response = await axios.get(
        `https://slateblue-mallard-430836.hostingersite.com/api/leave-types/company/${companyId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: (status) => status < 500
        }
      );

      console.log('¡ LEAVE TYPES API RESPONSE:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data,
        headers: response.headers
      });

      // Handle different response statuses
      if (response.status === 401) {
        console.error('š Unauthorized - Token expired or invalid');
        setLeaveTypesError('Session expired. Please log in again.');
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
        return;
      }

      if (response.status === 404) {
        console.warn('No leave types found for company');
        setLeaveTypesError('No leave types configured for your company. Please contact HR.');
        setLeaveTypes([]);
        return;
      }

      if (response.status !== 200) {
        throw new Error(`API returned status ${response.status}: ${response.statusText}`);
      }

      // Process successful response
      let fetchedLeaveTypes = [];

      if (response.data && Array.isArray(response.data)) {
        fetchedLeaveTypes = response.data;
      } else if (response.data.leaveTypes && Array.isArray(response.data.leaveTypes)) {
        fetchedLeaveTypes = response.data.leaveTypes;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        fetchedLeaveTypes = response.data.data;
      } else {
        console.warn("⚠️ Unexpected leave types response format:", response.data);
        setLeaveTypesError('Invalid response format from server.');
        setLeaveTypes([]);
        return;
      }

      console.log('🎯 PROCESSED LEAVE TYPES:', {
        count: fetchedLeaveTypes.length,
        leaveTypes: fetchedLeaveTypes.map(type => ({
          id: type._id || type.id,
          name: type.leaveType || type.name,
          description: type.description,
          daysAllowed: type.daysAllowed,
          carryForward: type.carryForward,
          active: type.active
        }))
      });

      setLeaveTypes(fetchedLeaveTypes);

      if (fetchedLeaveTypes.length === 0) {
        setLeaveTypesError('No leave types available. Please contact HR to configure leave types.');
      }

    } catch (err) {
      console.error("❌ Error fetching leave types:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });

      // Set appropriate error message based on error type
      if (err.response?.status === 401) {
        setLeaveTypesError('Session expired. Please log in again.');
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
      } else if (err.response?.status === 404) {
        setLeaveTypesError('No leave types found for your company. Please contact HR.');
      } else if (err.response?.status >= 500) {
        setLeaveTypesError('Server error. Please try again later or contact support.');
      } else if (err.code === 'NETWORK_ERROR' || !err.response) {
        setLeaveTypesError('Network error. Please check your connection and try again.');
      } else {
        setLeaveTypesError('Failed to load leave types. Please try again.');
      }

      setLeaveTypes([]);
    } finally {
      setLoadingLeaveTypes(false);
    }
  };

  // Fetch employee details by ID
  const fetchEmployeeDetails = async (employeeId) => {
    if (!employeeId) return;

    setLoading(true);
    setError(null);
    const token = localStorage.getItem("authToken") || localStorage.getItem("token");

    if (!token) {
      setError("❌ Access token not found. Please log in.");
      setLoading(false);
      return;
    }

    // ✅ Decode companyId from token
    let tokenCompanyId;
    try {
      const decoded = jwtDecode(token);
      let raw = decoded.companyId || decoded.company?._id || decoded.company || decoded.orgId;
      // If company is an object, extract string ID
      if (raw && typeof raw === 'object') {
        raw = raw._id || raw.id || null;
      }
      tokenCompanyId = raw ? String(raw) : null;
      console.log('🔑 Token company ID:', tokenCompanyId);
    } catch (err) {
      console.error("Error decoding token:", err);
      setError("❌ Invalid or expired token. Please log in again.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `https://slateblue-mallard-430836.hostingersite.com/api/employees/${employeeId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const employeeData = response.data;
      setEmployeeDetails(employeeData);

      const fullName =
        employeeData.teamMemberName ||
        (employeeData.firstName && employeeData.lastName
          ? `${employeeData.firstName} ${employeeData.lastName}`
          : employeeData.name || "Employee");

      // ✅ Properly extract company ID (could be string OR object with _id)
      const rawCompany = employeeData.company;
      let employeeCompanyId = null;
      if (rawCompany) {
        if (typeof rawCompany === 'object') {
          employeeCompanyId = rawCompany._id || rawCompany.id || null;
        } else {
          employeeCompanyId = String(rawCompany);
        }
      }

      // Best available company ID: prefer employee data, fall back to token
      const resolvedCompanyId = employeeCompanyId || tokenCompanyId;

      console.log('🏢 Employee company ID:', employeeCompanyId);
      console.log('🏢 Resolved company ID for leave types:', resolvedCompanyId);

      setFormData((prev) => ({
        ...prev,
        employeeName: fullName,
        contact: employeeData.emergencyMobileNumber || "",
        company: resolvedCompanyId || prev.company,
      }));

      localStorage.setItem("employeeName", fullName);

      // ✅ ALWAYS fetch ALL company leave types from Leave Type Management API
      // This shows hhh, Casual leave, sick leave, Annual leave + Unpaid Leave
      setLoadingLeaveTypes(true);
      setLeaveTypesError(null);

      if (resolvedCompanyId) {
        try {
          console.log('🚀 Fetching company leave types for ID:', resolvedCompanyId);
          const ltResponse = await axios.get(
            `https://slateblue-mallard-430836.hostingersite.com/api/leave-types/company/${resolvedCompanyId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
              validateStatus: (status) => status < 500
            }
          );

          console.log('📋 Leave types API response status:', ltResponse.status);
          console.log('📋 Leave types API data:', ltResponse.data);

          let companyLeaveTypes = [];
          if (ltResponse.status === 200) {
            if (Array.isArray(ltResponse.data)) {
              companyLeaveTypes = ltResponse.data;
            } else if (ltResponse.data?.leaveTypes && Array.isArray(ltResponse.data.leaveTypes)) {
              companyLeaveTypes = ltResponse.data.leaveTypes;
            } else if (ltResponse.data?.data && Array.isArray(ltResponse.data.data)) {
              companyLeaveTypes = ltResponse.data.data;
            }
          }

          console.log('✅ Company leave types:', companyLeaveTypes.length,
            companyLeaveTypes.map(t => t.leaveType || t.name));

          // Append Unpaid Leave at the end
          const withUnpaid = [
            ...companyLeaveTypes,
            {
              _id: 'unpaid-leave',
              id: 'unpaid-leave',
              leaveType: 'Unpaid Leave',
              name: 'Unpaid Leave',
              daysAllowed: Infinity,
              description: 'No limit - can request any number of days',
              isUnpaidLeave: true
            }
          ];

          setLeaveTypes(withUnpaid);
          setLeaveTypesError(null);
          setLoadingLeaveTypes(false);

        } catch (ltErr) {
          console.error('❌ Failed to fetch company leave types:', ltErr);
          setLeaveTypes([{
            _id: 'unpaid-leave', id: 'unpaid-leave',
            leaveType: 'Unpaid Leave', name: 'Unpaid Leave',
            daysAllowed: Infinity, isUnpaidLeave: true
          }]);
          setLeaveTypesError(null);
          setLoadingLeaveTypes(false);
        }
      } else {
        console.warn('⚠️ No company ID resolved — showing Unpaid Leave only');
        setLeaveTypes([{
          _id: 'unpaid-leave', id: 'unpaid-leave',
          leaveType: 'Unpaid Leave', name: 'Unpaid Leave',
          daysAllowed: Infinity, isUnpaidLeave: true
        }]);
        setLeaveTypesError(null);
        setLoadingLeaveTypes(false);
      }
    } catch (err) {
      console.error("Error fetching employee data:", err);
      setError("Failed to load employee details. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch leave requests from API
  const fetchLeaveRequests = async () => {
    setLoadingRequests(true);
    setError(null);
    const token = localStorage.getItem("authToken") || localStorage.getItem("token");

    if (!token) {
      setError("❌ Access token not found. Please log in again.");
      setLoadingRequests(false);
      return;
    }

    // ✅ Decode employeeId (and companyId if needed) from token
    let employeeId, companyId;
    try {
      const decoded = jwtDecode(token);
      employeeId = decoded.employeeId || decoded.id || decoded._id;
      companyId = decoded.companyId || decoded.orgId;
    } catch (err) {
      console.error("Error decoding token:", err);
      setError("❌ Invalid or expired token. Please log in again.");
      setLoadingRequests(false);
      return;
    }

    if (!employeeId) {
      setError("❌ No employee ID found in token.");
      setLoadingRequests(false);
      return;
    }

    try {
      // Fetch all leave requests for this employee
      const response = await axios.get(
        "https://slateblue-mallard-430836.hostingersite.com/api/leaves",
        {
          params: { employee: employeeId, company: companyId },
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          validateStatus: (status) => status < 500,
        }
      );

      if (response.status === 200) {
        let leaveRequests = [];

        if (response.data.leaves && Array.isArray(response.data.leaves)) {
          leaveRequests = response.data.leaves;
        } else if (Array.isArray(response.data)) {
          leaveRequests = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          leaveRequests = response.data.data;
        }

        setLeaveRequests(leaveRequests);
        setFilteredLeaveRequests(leaveRequests);
        calculateLeaveBalance(leaveRequests);
      } else {
        throw new Error(
          response.data?.message ||
          `Failed to fetch leave requests (${response.status})`
        );
      }
    } catch (err) {
      console.error("Error in fetchLeaveRequests:", err);
      setError(err.message || "Failed to load leave requests. Please try again.");
    } finally {
      setLoadingRequests(false);
    }
  };

  // Calculate leave balance from leave requests - UPDATED FOR PARTIAL APPROVALS
  const calculateLeaveBalance = (requests) => {
    if (!Array.isArray(requests)) {
      console.warn('Invalid leave requests data:', requests);
      return;
    }

    const today = new Date();
    const currentYear = today.getFullYear();

    // Initialize balance object for all leave types
    const balance = {};

    // Initialize with employee's paid leaves or default types
    if (leaveTypes.length > 0) {
      leaveTypes.forEach(type => {
        const leaveName = type.leaveType || type.name;
        balance[leaveName] = { used: 0 };
      });
    } else {
      // Fallback to default types
      balance['Casual Leave'] = { used: 0 };
      balance['Sick Leave'] = { used: 0 };
      balance['Annual Leave'] = { used: 0 };
    }

    requests.forEach(request => {
      if (!request) return;

      // Get the leave type from the request
      const leaveType = (request.leaveType || request.type || 'Leave').toString();

      // Skip unpaid leave - it doesn't consume paid balance
      if (leaveType.toLowerCase() === 'unpaid leave') return;

      // Get the status from the request
      const requestStatus = (request.status || '').toString().toLowerCase();
      const startDate = request.startDate || request.fromDate;
      const endDate = request.endDate || request.toDate;

      // Process approved AND partially approved leave requests with valid dates
      if ((requestStatus === 'approved' || requestStatus === 'partially approved') && startDate && endDate) {
        try {
          const start = new Date(startDate);
          const end = new Date(endDate);

          // Only count leaves from current year
          if (start.getFullYear() === currentYear) {
            let days = 0;

            // For partially approved leaves, use approvedDates array if available
            if (requestStatus === 'partially approved') {
              if (request.approvedDates && Array.isArray(request.approvedDates)) {
                days = request.approvedDates.length; // Number of approved dates
              } else if (request.approvedDays) {
                days = parseInt(request.approvedDays) || 0; // Fallback to number if provided
              } else {
                // Fallback to full days if no approved info
                days = calculateDays(start, end);
              }
            } else {
              // For fully approved leaves, calculate days between dates
              days = calculateDays(start, end);
            }

            // Add to the specific leave type balance
            if (!balance[leaveType]) {
              balance[leaveType] = { used: 0 };
            }
            balance[leaveType].used += days;
          }
        } catch (error) {
          console.error('Error processing leave request:', {
            request,
            error
          });
        }
      }
    });

    // Calculate remaining days for each leave type
    const newLeaveBalance = {};

    if (leaveTypes.length > 0) {
      // Use employee's allocated paid leaves
      leaveTypes.forEach(type => {
        const leaveName = type.leaveType || type.name;
        const totalDays = type.daysAllowed || 0;
        const usedDays = balance[leaveName]?.used || 0;

        newLeaveBalance[leaveName] = {
          total: totalDays,
          used: Math.min(usedDays, totalDays),
          remaining: Math.max(0, totalDays - usedDays)
        };
      });
    } else {
      // Fallback to default leave limits
      const leaveLimits = {
        'Casual Leave': 12,
        'Sick Leave': 10,
        'Annual Leave': 20
      };

      Object.keys(leaveLimits).forEach(leaveType => {
        const usedDays = balance[leaveType]?.used || 0;
        const totalDays = leaveLimits[leaveType];

        newLeaveBalance[leaveType] = {
          total: totalDays,
          used: Math.min(usedDays, totalDays),
          remaining: Math.max(0, totalDays - usedDays)
        };
      });
    }

    console.log('📊 FINAL LEAVE BALANCE CALCULATION:', newLeaveBalance);
    setLeaveBalance(newLeaveBalance);
  };

  // Load employee data
  const loadEmployeeData = async () => {
    const token = localStorage.getItem("authToken") || localStorage.getItem("token");

    if (!token) {
      setError("No access token found. Please log in again.");
      return;
    }

    let employeeId;
    try {
      const decoded = jwtDecode(token);
      const extractedCompanyId = decoded.companyId || decoded.company?._id || decoded.company || decoded.orgId;

      employeeId = decoded.employeeId || decoded.id || decoded._id;

      if (extractedCompanyId) {
        setFormData(prev => ({
          ...prev,
          company: extractedCompanyId
        }));
      }

    } catch (err) {
      console.error("Error decoding token:", err);
      setError("Invalid or expired token. Please log in again.");
      return;
    }

    if (employeeId) {
      await fetchEmployeeDetails(employeeId);
    } else {
      setError("No employee ID found. Please log in again.");
    }
  };

  // Load employee details and leave requests on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load employee data first (this sets leaveTypes)
        await loadEmployeeData();
        // Then load leave requests (this will calculate balance with leaveTypes available)
        await fetchLeaveRequests();
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data. Please try again.');
      }
    };

    loadData();
  }, []);

  // Recalculate balance whenever leaveTypes changes (e.g., after fetchEmployeeDetails completes)
  useEffect(() => {
    if (leaveTypes.length > 0 && leaveRequests.length >= 0) { // leaveRequests might be empty initially
      console.log('„ Recalculating balance due to leaveTypes change');
      calculateLeaveBalance(leaveRequests);
    }
  }, [leaveTypes, leaveRequests]);

  // Initialize selected leave balance when leave types or form data changes
  useEffect(() => {
    if (formData.leaveType && formData.leaveType.toLowerCase() === 'unpaid leave') {
      setSelectedLeaveBalance({ total: Infinity, used: 0, remaining: Infinity });
    } else if (formData.leaveType && leaveBalance[formData.leaveType]) {
      setSelectedLeaveBalance(leaveBalance[formData.leaveType]);
    } else if (leaveTypes.length > 0 && !formData.leaveType) {
      // Set default selected balance to first leave type
      const firstLeaveType = leaveTypes[0].leaveType || leaveTypes[0].name;
      if (leaveBalance[firstLeaveType]) {
        setSelectedLeaveBalance(leaveBalance[firstLeaveType]);
        // Also set the form data to first leave type
        setFormData(prev => ({
          ...prev,
          leaveType: firstLeaveType
        }));
      }
    }
  }, [leaveBalance, leaveTypes, formData.leaveType]);

  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      const employeeId = localStorage.getItem("selectedEmployeeId");
      const companyId = localStorage.getItem("selectedCompanyId");

      if (!token || !employeeId) {
        // Optionally redirect to login
        // navigate('/login');
      } else {
        // Update the state with the correct IDs
        setSelectedEmployeeId(employeeId);
        setFormData(prev => ({
          ...prev,
          company: companyId,
          employee: employeeId
        }));
      }
    };

    checkAuth();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value
    });

    // Update selected leave balance when leave type changes
    if (name === 'leaveType' && value) {
      if (value.toLowerCase() === 'unpaid leave') {
        setSelectedLeaveBalance({ total: Infinity, used: 0, remaining: Infinity });
      } else {
        const selectedBalance = leaveBalance[value] || { total: 0, used: 0, remaining: 0 };
        setSelectedLeaveBalance(selectedBalance);
      }
    }
  };

  // Handle edit action
  const handleEdit = (id) => {
    setEditingId(id);
    const requestToEdit = leaveRequests.find(request => {
      const requestId = request._id || request.id;
      return requestId === id;
    });

    if (requestToEdit) {

      const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try {
          const date = new Date(dateString);
          return date.toISOString().split('T')[0]; // Converts to YYYY-MM-DD
        } catch (e) {
          return '';
        }
      };

      setFormData({
        ...formData,
        startDate: formatDateForInput(requestToEdit.startDate || requestToEdit.fromDate),
        endDate: formatDateForInput(requestToEdit.endDate || requestToEdit.toDate),
        reason: requestToEdit.reason || '',
        contact: requestToEdit.contact || employeeDetails?.emergencyMobileNumber || '',
        leaveType: requestToEdit.leaveType || requestToEdit.type || ''
      });
    }
  };

  // Cancel edit mode
  const cancelEdit = () => {
    setEditingId(null);
    setFormData(prev => ({
      ...prev,
      startDate: '',
      endDate: '',
      reason: '',
      fromDate: '',
      toDate: '',
      leaveType: ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Check for authentication token
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");

      // Validation
      if (!token) {
        throw new Error('No authentication token found. Please log in again.');
      }

      // Decode JWT token to extract employeeId and companyId
      let employeeId, companyId;
      try {
        const decoded = jwtDecode(token);

        // Extract employeeId - this should be the logged-in user
        employeeId = decoded.employeeId || decoded._id || decoded.id;

        // Extract companyId - look for company information in different possible fields
        companyId = decoded.companyId ||
          decoded.company?._id ||
          decoded.company?.id ||
          decoded.company ||
          decoded.orgId ||
          decoded.organizationId;

        // If no separate company ID found, we need to get it from employee data
        if (!companyId && employeeDetails?.company) {
          companyId = employeeDetails.company._id || employeeDetails.company;
        }

      } catch (err) {
        console.error("Error decoding token:", err);
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
        throw new Error('Invalid or expired token. Please log in again.');
      }

      if (!employeeId) {
        throw new Error('Employee ID is missing from token. Please log in again.');
      }

      // If companyId is still not found, try to get it from localStorage or fetch employee details
      if (!companyId) {

        // Try localStorage first
        const storedCompanyId = localStorage.getItem('selectedCompanyId');
        if (storedCompanyId) {
          companyId = storedCompanyId;
        } else {
          // If still no companyId, we need to fetch employee details to get company info
          try {
            const empResponse = await axios.get(
              `https://slateblue-mallard-430836.hostingersite.com/api/employees/${employeeId}`,
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );

            if (empResponse.data?.company) {
              companyId = empResponse.data.company._id || empResponse.data.company;
            }
          } catch (empErr) {
            console.error('Failed to fetch employee details for company ID:', empErr);
          }
        }
      }

      if (!companyId) {
        throw new Error('Company information is missing. Please contact support.');
      }

      // Validate form fields
      if (!formData.reason || !formData.reason.trim()) {
        throw new Error('Please provide a reason for your leave request.');
      }

      const startDate = formData.fromDate || formData.startDate;
      const endDate = formData.toDate || formData.endDate;

      if (!startDate || !endDate) {
        throw new Error('Please select both start and end dates for your leave.');
      }

      if (new Date(startDate) > new Date(endDate)) {
        throw new Error('End date must be after or equal to start date.');
      }

      // Check leave balance (skip for Unpaid Leave)
      if (formData.leaveType && formData.leaveType.toLowerCase() !== 'unpaid leave' && selectedLeaveBalance.remaining <= 0) {
        throw new Error(`No available balance for ${formData.leaveType}. Please select a different leave type.`);
      }

      // Calculate requested days
      const requestedDays = calculateDays(new Date(startDate), new Date(endDate));

      // Check if requested days exceed available balance (skip for Unpaid Leave)
      if (formData.leaveType && formData.leaveType.toLowerCase() !== 'unpaid leave' && requestedDays > selectedLeaveBalance.remaining) {
        throw new Error(`Requested ${requestedDays} days exceeds available balance of ${selectedLeaveBalance.remaining} days for ${formData.leaveType}.`);
      }

      setSubmitting(true);
      setError(null);

      // Prepare request data (same for create and update)
      const requestData = {
        company: companyId,
        employee: employeeId,
        reason: formData.reason.trim(),
        fromDate: startDate,
        toDate: endDate,
        contact: formData.contact || '',
        leaveType: formData.leaveType || 'Leave',
        status: 'Pending', // Or keep existing status if updating
        appliedDate: new Date().toISOString()
      };

      let response;
      if (editingId) {
        // Update existing request using PATCH (instead of PUT)
        response = await axios.patch(
          `https://slateblue-mallard-430836.hostingersite.com/api/leaves/${editingId}`,
          requestData,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );
      } else {
        // Create new request
        response = await axios.post(
          'https://slateblue-mallard-430836.hostingersite.com/api/leaves',
          requestData,
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          }
        );
      }

      // Handle successful submission (create or update)
      setFormData({
        company: companyId,
        employee: employeeId,
        reason: '',
        fromDate: '',
        toDate: '',
        startDate: '',
        endDate: '',
        contact: formData.contact,
        employeeName: formData.employeeName,
        leaveType: ''
      });

      // Reset edit mode if it was an update
      if (editingId) {
        setEditingId(null);
      }

      setShowAlert(editingId ? 'Leave request updated successfully!' : 'Leave request submitted successfully!');

      // Refresh the leave requests
      setTimeout(() => {
        fetchLeaveRequests().catch(err => {
          console.error('Error refreshing leave requests:', err);
          setError('Request processed but failed to refresh the list. Please refresh the page.');
        });
      }, 500);

    } catch (err) {
      console.error('Error submitting leave request:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        fullError: err
      });

      // Log the full response for debugging
      if (err.response) {
        console.error('Full API response:', err.response);
        console.error('Response data:', err.response.data);
        console.error('Response status:', err.response.status);
        console.error('Response headers:', err.response.headers);
      }

      let errorMessage = 'Failed to submit leave request';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.response?.data) {
        errorMessage = JSON.stringify(err.response.data);
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // NEW: Handle delete confirmation
  const handleDeleteBranch = (requestId, requestName) => {
    setConfirmAction({
      type: 'delete',
      message: `Are you sure you want to delete the leave request "${requestName}"?`,
      requestId: requestId
    });
  };

  // NEW: Execute the confirmed action
  const handleConfirmAction = async () => {
    const token = localStorage.getItem("authToken") || localStorage.getItem("token");

    if (!token) {
      setError('No access token found. Please log in.');
      setConfirmAction(null);
      return;
    }

    try {
      setDeleting(true);

      if (confirmAction.type === 'delete') {
        // First find the request to ensure it exists and get its details
        const requestToDelete = leaveRequests.find(request => {
          const requestId = request._id || request.id;
          return requestId === confirmAction.requestId;
        });

        if (!requestToDelete) {
          setError('Leave request not found');
          setConfirmAction(null);
          return;
        }

        // Use the correct endpoint and method
        await axios.delete(`https://slateblue-mallard-430836.hostingersite.com/api/leaves/${confirmAction.requestId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        setShowAlert('Leave request deleted successfully!');
        fetchLeaveRequests();
      }

    } catch (err) {
      console.error("Error performing leave request operation:", err.response?.data || err.message || err);
      if (err.response?.status === 401) {
        setError('Session expired. Please log in again.');
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
      } else {
        const errorMessage = err.response?.data?.message || `Error ${confirmAction.type}ing leave request. Please try again.`;
        setError(errorMessage);
      }
    } finally {
      setDeleting(false);
      setConfirmAction(null);
    }
  };

  // NEW: Close confirmation dialog
  const handleCloseAlert = () => {
    setConfirmAction(null);
  };

  // NEW: Close success alert
  const handleCloseSuccessAlert = () => {
    setShowAlert(null);
  };

  // NEW: Close error alert
  const handleCloseErrorAlert = () => {
    setError(null);
  };

  // Calculate number of days between dates
  const calculateDays = (start, end) => {
    try {
      // Handle both string and Date objects
      const startDate = start instanceof Date ? start : new Date(start);
      const endDate = end instanceof Date ? end : new Date(end);

      // Check if dates are valid
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        console.error('Invalid date range:', { start, end });
        return 0;
      }

      // Calculate difference in days (inclusive of both start and end dates)
      const diffTime = Math.abs(endDate - startDate);
      const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

      // Ensure we don't return negative days
      return Math.max(0, days);
    } catch (error) {
      console.error('Error calculating days between dates:', { start, end, error });
      return 0;
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return '#4caf50';
      case 'Pending': return '#ff9800';
      case 'Rejected': return '#f44336';
      case 'Partially Approved': return '#ff9800';
      default: return '#9e9e9e';
    }
  };

  // Filter leave requests by month and year
  const filterLeaveRequests = () => {
    let filtered = [...leaveRequests];

    if (filterMonth || filterYear) {
      filtered = leaveRequests.filter(request => {
        if (!request) return false;

        const startDate = request.fromDate || request.startDate;
        if (!startDate) return false;

        try {
          const requestDate = new Date(startDate);
          const requestMonth = requestDate.getMonth() + 1;
          const requestYear = requestDate.getFullYear();

          let matchesMonth = true;
          let matchesYear = true;

          if (filterMonth) {
            matchesMonth = requestMonth === parseInt(filterMonth);
          }

          if (filterYear) {
            matchesYear = requestYear === parseInt(filterYear);
          }

          return matchesMonth && matchesYear;
        } catch (error) {
          console.error('Error filtering request:', error);
          return false;
        }
      });
    }

    setFilteredLeaveRequests(filtered);
  };

  // Handle filter changes
  const handleMonthChange = (e) => {
    setFilterMonth(e.target.value);
  };

  const handleYearChange = (e) => {
    setFilterYear(e.target.value);
  };

  // Clear filters
  const clearFilters = () => {
    setFilterMonth('');
    setFilterYear('');
    setFilteredLeaveRequests(leaveRequests);
  };

  // Generate year options (current year and past 5 years)
  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 6; i++) {
      years.push(currentYear - i);
    }
    return years;
  };

  // Apply filters when filter values or leave requests change
  React.useEffect(() => {
    filterLeaveRequests();
  }, [filterMonth, filterYear, leaveRequests]);

  return (

    <div style={styles.container}>
      <h2 style={styles.heading}>Leave Request</h2>

      <div style={styles.content}>
        {/* Leave Request Form */}
        <div style={styles.formSection}>
          <h3 style={styles.sectionHeading}>
            {editingId ? 'Edit Leave Request' : 'Apply for Leave'}
          </h3>
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Employee Name</label>
                <div style={{
                  padding: '10px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  minHeight: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  color: '#333',
                  fontWeight: '500',
                  marginBottom: '10px'
                }}>
                  {formData.employeeName || 'Not available'}
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Contact During Leave</label>
                <input
                  type="text"
                  name="contact"
                  value={formData.contact}
                  onChange={handleInputChange}
                  style={styles.input}
                  placeholder="Phone number"
                  required
                />
              </div>
            </div>

            {/* SINGLE LEAVE TYPE DROPDOWN - REMOVED DUPLICATE */}
            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Leave Type
                  {loadingLeaveTypes && (
                    <span style={{ marginLeft: '8px', color: '#3498db' }}>
                      <span style={{
                        display: 'inline-block',
                        width: '12px',
                        height: '12px',
                        border: '2px solid #3498db',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></span>
                      Loading...
                    </span>
                  )}

                  {!loadingLeaveTypes && !leaveTypesError && leaveTypes.length > 0 && (
                    <span style={{ marginLeft: '8px', color: '#27ae60', fontSize: '12px' }}>
                      {leaveTypes[0]?.isEmployeePaidLeave
                        ? `–ï ${leaveTypes.length} paid leave${leaveTypes.length !== 1 ? 's' : ''} allocated`
                        : `✅ ${leaveTypes.length} types available`
                      }
                    </span>
                  )}
                </label>

                <select
                  name="leaveType"
                  value={formData.leaveType}
                  onChange={handleInputChange}
                  style={{
                    ...styles.select,
                    backgroundColor: loadingLeaveTypes ? '#f8f9fa' : 'white',
                    cursor: loadingLeaveTypes ? 'not-allowed' : 'pointer'
                  }}
                  disabled={loadingLeaveTypes}
                  required
                >
                  <option value="">
                    {loadingLeaveTypes
                      ? 'Loading leave types...'
                      : leaveTypes.length === 0
                        ? 'No leave types available'
                        : leaveTypes[0]?.isEmployeePaidLeave
                          ? 'Select from your allocated leaves'
                          : 'Select Leave Type'
                    }
                  </option>
                  {!loadingLeaveTypes && leaveTypes.map(type => {
                    const leaveName = type.leaveType || type.name;
                    const balanceInfo = leaveBalance[leaveName] || { total: type.daysAllowed || 0, used: 0, remaining: type.daysAllowed || 0 };

                    // Special handling for Unpaid Leave
                    const isUnpaid = type.isUnpaidLeave || leaveName.toLowerCase() === 'unpaid leave';
                    const displayBalance = isUnpaid ? 'Unlimited' : `${balanceInfo.remaining}/${balanceInfo.total} days available`;

                    return (
                      <option key={type._id || type.id} value={leaveName}>
                        {leaveName}
                        {!isUnpaid && ` (${balanceInfo.remaining}/${balanceInfo.total} days available)`}
                        {isUnpaid && ' '}
                      </option>
                    );
                  })}
                </select>

                {/* Selected Leave Balance Display */}
                {formData.leaveType && selectedLeaveBalance && (
                  <div style={{
                    marginTop: '8px',
                    padding: '10px',
                    backgroundColor: selectedLeaveBalance.remaining > 0 || formData.leaveType.toLowerCase() === 'unpaid leave' ? '#e8f5e8' : '#ffe8e8',
                    border: `1px solid ${selectedLeaveBalance.remaining > 0 || formData.leaveType.toLowerCase() === 'unpaid leave' ? '#4caf50' : '#f44336'}`,
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontWeight: '500'
                    }}>
                      <span style={{ color: '#2c3e50' }}>
                        {formData.leaveType} Balance:
                      </span>
                      <span style={{
                        color: formData.leaveType.toLowerCase() === 'unpaid leave' ? '#27ae60' : (selectedLeaveBalance.remaining > 0 ? '#27ae60' : '#e74c3c'),
                        fontWeight: '600'
                      }}>
                        {formData.leaveType.toLowerCase() === 'unpaid leave' ? '' : `${selectedLeaveBalance.remaining} days remaining`}
                      </span>
                    </div>

                    {formData.leaveType.toLowerCase() !== 'unpaid leave' && (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '4px',
                        fontSize: '12px',
                        color: '#7f8c8d'
                      }}>
                        <span>Total: {selectedLeaveBalance.total} days</span>
                        <span>Used: {selectedLeaveBalance.used} days</span>
                      </div>
                    )}

                    {/* Progress Bar - Skip for Unpaid Leave */}
                    {formData.leaveType.toLowerCase() !== 'unpaid leave' && (
                      <div style={{
                        marginTop: '8px',
                        height: '6px',
                        backgroundColor: '#ecf0f1',
                        borderRadius: '3px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          height: '100%',
                          width: `${(selectedLeaveBalance.used / selectedLeaveBalance.total) * 100}%`,
                          backgroundColor: selectedLeaveBalance.remaining > 0 ? '#3498db' : '#e74c3c',
                          transition: 'width 0.3s ease'
                        }}></div>
                      </div>
                    )}

                    {/* Warning for insufficient balance - Skip for Unpaid Leave */}
                    {formData.leaveType.toLowerCase() !== 'unpaid leave' && selectedLeaveBalance.remaining <= 0 && (
                      <div style={{
                        marginTop: '6px',
                        padding: '4px 8px',
                        backgroundColor: '#ffebee',
                        border: '1px solid #ffcdd2',
                        borderRadius: '4px',
                        color: '#c62828',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        ⚠️ No available balance for this leave type
                      </div>
                    )}
                  </div>
                )}


              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                  style={styles.input}
                  min={new Date().toISOString().split('T')[0]}  // Restricts to today onwards
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleInputChange}
                  style={styles.input}
                  min={new Date().toISOString().split('T')[0]}  // Restricts to today onwards
                  required
                />
              </div>
            </div>


            <div style={styles.formGroup}>
              <label style={styles.label}>Reason for Leave</label>
              <textarea
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                style={styles.textarea}
                placeholder="Please provide a reason for your leave"
                required
              />
            </div>

            <div style={styles.buttonGroup}>
              <button type="submit" style={styles.submitButton} disabled={submitting}>
                {submitting ? 'Submitting...' : editingId ? 'Update Request' : 'Submit Request'}
              </button>

              {editingId && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  style={styles.cancelButton}
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Leave Balance and History */}
        <div style={styles.historySection}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionHeading}>Your Leave Requests</h3>
            <div style={styles.requestsCount}>
              {filteredLeaveRequests.length} of {leaveRequests.length} {leaveRequests.length === 1 ? 'Request' : 'Requests'}
            </div>
          </div>

          {/* Filter Controls */}
          <div style={styles.filterContainer}>
            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Filter by Month:</label>
              <select
                value={filterMonth}
                onChange={handleMonthChange}
                style={styles.filterSelect}
              >
                <option value="">All Months</option>
                <option value="1">January</option>
                <option value="2">February</option>
                <option value="3">March</option>
                <option value="4">April</option>
                <option value="5">May</option>
                <option value="6">June</option>
                <option value="7">July</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
            </div>

            <div style={styles.filterGroup}>
              <label style={styles.filterLabel}>Filter by Year:</label>
              <select
                value={filterYear}
                onChange={handleYearChange}
                style={styles.filterSelect}
              >
                <option value="">All Years</option>
                {getYearOptions().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {(filterMonth || filterYear) && (
              <button
                onClick={clearFilters}
                style={styles.clearFiltersButton}
                title="Clear all filters"
              >
                Clear Filters
              </button>
            )}
          </div>
          {loadingRequests ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>Loading leave requests...</div>
          ) : error ? (
            <div style={{ color: '#e74c3c', padding: '10px', backgroundColor: '#fdecea', borderRadius: '4px', marginBottom: '15px' }}>
              {error}
            </div>
          ) : filteredLeaveRequests.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#7f8c8d' }}>
              {leaveRequests.length === 0 ? 'No leave requests found.' : 'No leave requests match the selected filters.'}
            </div>
          ) : (
            <div style={styles.requestsList}>
              {filteredLeaveRequests.map(request => {
                if (!request) return null;

                try {
                  // Safely get request data with fallbacks
                  const requestId = request._id || request.id || Math.random().toString(36).substr(2, 9);
                  const leaveType = request.leaveType || request.type || 'Leave';
                  const status = request.status || 'Pending';
                  const startDate = request.fromDate || request.startDate || new Date().toISOString();
                  const endDate = request.toDate || request.endDate || new Date().toISOString();
                  const reason = request.reason || 'No reason provided';
                  const contact = request.contact ||
                    (request.employee && (request.employee.emergencyMobileNumber || request.employee.phone)) ||
                    employeeDetails?.emergencyMobileNumber ||
                    'N/A';

                  // Format dates
                  const formatDate = (dateString) => {
                    try {
                      return new Date(dateString).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      });
                    } catch (e) {
                      return 'Invalid date';
                    }
                  };

                  // Get applied date with fallback to current date if not available
                  const appliedDate = request.appliedDate || request.createdAt || new Date().toISOString();

                  // Check if action buttons should be shown
                  const showActionButtons = status !== 'Approved' && status !== 'Partially Approved';

                  return (
                    <div key={requestId} style={styles.requestCard}>
                      <div style={styles.requestHeader}>
                        <div>
                          <span style={styles.requestType}>{leaveType}</span>
                          <span style={{
                            ...styles.requestStatus,
                            backgroundColor: getStatusColor(status),
                            textTransform: 'capitalize'
                          }}>
                            {status.toLowerCase()}
                          </span>
                          {/* Show approved days for partially approved leaves */}
                          {status.toLowerCase() === 'partially approved' && request.approvedDates && (
                            <span style={{
                              marginLeft: '8px',
                              padding: '2px 6px',
                              backgroundColor: '#e3f2fd',
                              color: '#1976d2',
                              borderRadius: '8px',
                              fontSize: '0.7rem',
                              fontWeight: '500'
                            }}>
                              {request.approvedDates.length} days approved
                            </span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: '#7f8c8d', marginTop: '4px' }}>
                          Applied on: {formatDate(appliedDate)}
                        </div>
                      </div>
                      <div style={styles.dateSection}>
                        <div style={styles.dateRangeContainer}>
                          <div style={styles.dateGroup}>
                            <div style={styles.dateLabel}>From</div>
                            <div style={styles.dateValue}>{formatDate(startDate)}</div>
                          </div>
                          <div style={styles.dateSeparator}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#adb5bd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="5" y1="12" x2="19" y2="12"></line>
                              <polyline points="12 5 19 12 12 19"></polyline>
                            </svg>
                          </div>
                          <div style={styles.dateGroup}>
                            <div style={styles.dateLabel}>To</div>
                            <div style={styles.dateValue}>{formatDate(endDate)}</div>
                          </div>
                        </div>
                        <div style={styles.durationBadge}>
                          <span style={styles.durationIcon}>ï</span>
                          <span>{calculateDays(startDate, endDate)} {calculateDays(startDate, endDate) === 1 ? 'Day' : 'Days'}</span>
                        </div>
                      </div>
                      {reason && <p style={styles.requestReason}><strong>Reason:</strong> {reason}</p>}

                      {/* UPDATED: Conditionally render action buttons */}
                      {showActionButtons && (
                        <div style={styles.actionButtons}>
                          <button
                            onClick={() => handleEdit(requestId)}
                            style={styles.editButton}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteBranch(requestId, leaveType)}
                            style={styles.deleteButton}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  );
                } catch (error) {
                  console.error('Error rendering leave request:', { request, error });
                  return null;
                }
              })}
            </div>
          )}
        </div>
      </div>

      {/* NEW: Confirmation Alert Dialog */}
      {confirmAction && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
            padding: '24px',
            width: '90%',
            maxWidth: '400px',
            textAlign: 'center'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#2c3e50',
              marginBottom: '16px'
            }}>Confirmation</h3>
            <p style={{
              color: '#666',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>{confirmAction.message}</p>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '12px'
            }}>
              <button
                onClick={handleConfirmAction}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.2s',
                  opacity: deleting ? 0.6 : 1
                }}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Confirm'}
              </button>
              <button
                onClick={handleCloseAlert}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.2s',
                  opacity: deleting ? 0.6 : 1
                }}
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Success Alert Dialog */}
      {showAlert && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
            padding: '24px',
            width: '90%',
            maxWidth: '400px',
            textAlign: 'center'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#2c3e50',
              marginBottom: '16px'
            }}>Success</h3>
            <p style={{
              color: '#666',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>{showAlert}</p>
            <div style={{
              display: 'flex',
              justifyContent: 'center'
            }}>
              <button
                onClick={handleCloseSuccessAlert}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Error Alert Dialog */}
      {error && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
            padding: '24px',
            width: '90%',
            maxWidth: '400px',
            textAlign: 'center'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#e74c3c',
              marginBottom: '16px'
            }}>Error</h3>
            <p style={{
              color: '#666',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>{error}</p>
            <div style={{
              display: 'flex',
              justifyContent: 'center'
            }}>
              <button
                onClick={handleCloseErrorAlert}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>

  );
};

// Add CSS animation for spinner
const spinKeyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Inject the keyframes into the document head
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.type = 'text/css';
  styleSheet.innerText = spinKeyframes;
  document.head.appendChild(styleSheet);
}

// Inline CSS styles
const styles = {
  container: {
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    padding: '20px',
    backgroundColor: '#f5f7f9',
    minHeight: '100vh'
  },
  heading: {
    color: '#2c3e50',
    marginBottom: '25px',
    fontSize: '28px',
    fontWeight: '600',
    borderBottom: '2px solid #3498db',
    paddingBottom: '10px'
  },
  content: {
    display: 'flex',
    flexDirection: 'row',
    gap: '30px',
    alignItems: 'flex-start'
  },
  formSection: {
    flex: '1',
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '25px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
  },
  historySection: {
    flex: '1',
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '25px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
  },
  sectionHeading: {
    color: '#2c3e50',
    marginTop: '0',
    marginBottom: '20px',
    fontSize: '20px',
    fontWeight: '600'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  formRow: {
    display: 'flex',
    gap: '15px'
  },
  formGroup: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '15px'
  },
  label: {
    marginBottom: '8px',
    fontWeight: '500',
    color: '#34495e'
  },
  input: {
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '16px',
    transition: 'border-color 0.3s',
    outline: 'none'
  },
  textarea: {
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '16px',
    minHeight: '80px',
    resize: 'vertical',
    transition: 'border-color 0.3s',
    outline: 'none',
    fontFamily: 'inherit'
  },
  buttonGroup: {
    display: 'flex',
    gap: '15px',
    marginTop: '10px'
  },
  submitButton: {
    padding: '14px 20px',
    backgroundColor: '#3498db',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s'
  },
  cancelButton: {
    padding: '14px 20px',
    backgroundColor: '#95a5a6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.3s'
  },
  balanceContainer: {
    display: 'flex',
    gap: '15px',
    marginBottom: '25px'
  },
  balanceCard: {
    flex: '1',
    backgroundColor: '#f8f9fa',
    padding: '15px',
    borderRadius: '8px',
    textAlign: 'center',
    border: '1px solid #e9ecef'
  },
  balanceTitle: {
    margin: '0 0 10px 0',
    fontSize: '14px',
    color: '#6c757d'
  },
  balanceCount: {
    margin: '0',
    fontSize: '18px',
    fontWeight: '600',
    color: '#2c3e50'
  },
  balanceRemaining: {
    fontSize: '14px',
    color: '#6c757d',
    marginLeft: '5px'
  },
  subHeading: {
    color: '#2c3e50',
    marginBottom: '15px',
    fontSize: '18px',
    fontWeight: '600'
  },
  requestsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
    paddingBottom: '10px',
    borderBottom: '1px solid #e9ecef'
  },
  requestsCount: {
    backgroundColor: '#e9f5ff',
    color: '#0066cc',
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '0.85rem',
    fontWeight: '500'
  },
  requestCard: {
    border: '1px solid #e9ecef',
    borderRadius: '12px',
    padding: '18px',
    backgroundColor: 'white',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      transform: 'translateY(-2px)'
    }
  },
  requestType: {
    color: '#2c3e50',
    fontWeight: '600',
    marginRight: '10px',
    textTransform: 'capitalize',
    fontSize: '1rem',
    display: 'inline-flex',
    alignItems: 'center',
    '&:after': {
      content: '"•"',
      margin: '0 8px',
      color: '#dee2e6'
    }
  },
  requestStatus: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: '600',
    color: 'white',
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: '4px',
    textTransform: 'capitalize',
    '&:first-letter': {
      textTransform: 'uppercase'
    }
  },
  dateSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '16px 0',
    padding: '12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px dashed #e9ecef'
  },
  dateRangeContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap'
  },
  dateGroup: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '100px'
  },
  dateLabel: {
    fontSize: '0.75rem',
    color: '#6c757d',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '4px',
    fontWeight: '600'
  },
  dateValue: {
    fontSize: '0.95rem',
    color: '#2c3e50',
    fontWeight: '500'
  },
  dateSeparator: {
    display: 'flex',
    alignItems: 'center',
    color: '#adb5bd',
    margin: '0 4px'
  },
  durationBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#e9f5ff',
    color: '#0066cc',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '0.85rem',
    fontWeight: '500',
    marginLeft: '12px'
  },
  durationIcon: {
    fontSize: '0.9em',
    display: 'flex',
    alignItems: 'center'
  },
  daysCount: {
    backgroundColor: '#f8f9fa',
    color: '#495057',
    fontSize: '0.8rem',
    padding: '2px 8px',
    borderRadius: '10px',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    '&:before': {
      content: '"•"',
      marginRight: '4px',
      color: '#dee2e6'
    }
  },
  requestReason: {
    color: '#495057',
    margin: '12px 0',
    fontSize: '0.95rem',
    lineHeight: '1.6',
    padding: '12px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    borderLeft: '3px solid #dee2e6'
  },
  requestDetails: {
    fontSize: '13px',
    color: '#6c757d',
    marginBottom: '15px'
  },
  actionButtons: {
    display: 'flex',
    gap: '10px',
    marginTop: '16px',
    justifyContent: 'flex-end',
    paddingTop: '12px',
    borderTop: '1px dashed #e9ecef'
  },
  editButton: {
    padding: '6px 14px',
    backgroundColor: '#f8f9fa',
    color: '#495057',
    border: '1px solid #dee2e6',
    borderRadius: '6px',
    borderRadius: '4px',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'background-color 0.3s'
  },
  deleteButton: {
    padding: '8px 15px',
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'background-color 0.3s'
  },
  filterContainer: {
    display: 'flex',
    gap: '15px',
    alignItems: 'flex-end',
    marginBottom: '20px',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid #e9ecef',
    flexWrap: 'wrap'
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '150px'
  },
  filterLabel: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#495057',
    marginBottom: '5px'
  },
  filterSelect: {
    padding: '8px 12px',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: 'white',
    color: '#495057',
    cursor: 'pointer',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    outline: 'none'
  },
  clearFiltersButton: {
    padding: '8px 16px',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    height: 'fit-content',
    marginTop: '20px'
  },
  select: {
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '16px',
    transition: 'border-color 0.3s',
    outline: 'none',
    backgroundColor: 'white',
    cursor: 'pointer'
  }
};

// Add hover effects
Object.assign(styles.input, {
  ':hover': {
    borderColor: '#3498db'
  }
});

Object.assign(styles.textarea, {
  ':hover': {
    borderColor: '#3498db'
  }
});

Object.assign(styles.submitButton, {
  ':hover': {
    backgroundColor: '#2980b9'
  }
});

Object.assign(styles.cancelButton, {
  ':hover': {
    backgroundColor: '#7f8c8d'
  }
});

Object.assign(styles.editButton, {
  ':hover': {
    backgroundColor: '#e67e22'
  },
  ':disabled': {
    backgroundColor: '#bdc3c7',
    cursor: 'not-allowed'
  }
});

Object.assign(styles.deleteButton, {
  ':hover': {
    backgroundColor: '#c0392b'
  },
  ':disabled': {
    backgroundColor: '#bdc3c7',
    cursor: 'not-allowed'
  }
});

Object.assign(styles.filterSelect, {
  ':hover': {
    borderColor: '#80bdff'
  },
  ':focus': {
    borderColor: '#80bdff',
    boxShadow: '0 0 0 0.2rem rgba(0, 123, 255, 0.25)'
  }
});

Object.assign(styles.clearFiltersButton, {
  ':hover': {
    backgroundColor: '#5a6268'
  }
});

Object.assign(styles.select, {
  ':hover': {
    borderColor: '#3498db'
  },
  ':focus': {
    borderColor: '#3498db',
    boxShadow: '0 0 0 0.2rem rgba(52, 152, 219, 0.25)'
  }
});

export default LeaveRequest;

